/**
 * Contact, and who may cross whose border (ADR-0060).
 *
 * At peace, a civilisation's land is closed to every civilisation that has met it,
 * unless the two have agreed open borders; at war, it is open to invasion. Strangers
 * may walk in, which is how civilisations meet. Units caught inside a
 * border they may not be in — because peace was made, open borders ended, or the border
 * grew over them — are sent home when the next turn begins.
 */

import type { Content } from '../../../content/schema/index.js';
import type { GameEvent } from '../commands/types.js';
import { isCoastalCity } from '../cities/production.js';
import { tileIndex } from '../state/grid.js';
import { wrappedDistance } from '../state/start.js';
import { NONE, VIS_VISIBLE, type City, type GameState, type Id } from '../state/types.js';
import { recomputeVision } from '../vision/index.js';
import { ensureRelation, relationOf } from './relation.js';

/** Whether a unit of `civId` may stand on this tile. */
export function mayEnter(state: GameState, civId: Id, index: number): boolean {
  const owner = state.map.owner[index]!;
  if (owner === NONE || owner === civId) return true;
  const relation = relationOf(state, civId, owner);
  // Strangers may wander in — that is how civilisations meet. Once they have met, the
  // border means something.
  if (!relation) return true;
  return relation.war || relation.openBorders;
}

/** Whether two civilisations know of each other: they have history, or one has seen the other. */
export function inContact(state: GameState, x: Id, y: Id): boolean {
  if (x === y) return false;
  return relationOf(state, x, y) !== null;
}

/** Civilisations this one has met, ascending. */
export function contactsOf(state: GameState, civId: Id): Id[] {
  const out: Id[] = [];
  for (const r of state.relations) {
    if (r.a === civId && state.civs[r.b] && !state.civs[r.b]!.defeated) out.push(r.b);
    else if (r.b === civId && state.civs[r.a] && !state.civs[r.a]!.defeated) out.push(r.a);
  }
  return out.sort((p, q) => p - q);
}

/**
 * Record first meetings: a civilisation that sees another's unit, or remembers one of
 * its cities, has met it — and so has the one it saw. Run when a turn begins and after
 * anything that changes what is seen, so contact is never more than a turn late.
 */
export function noteContacts(state: GameState): void {
  const ids = Object.keys(state.civs)
    .map(Number)
    .sort((a, b) => a - b);
  const width = state.map.width;
  for (const civId of ids) {
    const civ = state.civs[civId];
    if (!civ || civ.defeated) continue;
    const vision = state.vision[civId];
    const memory = state.memory[civId];
    const seen = new Set<Id>();
    if (memory) for (const city of Object.values(memory.cities)) if (city.owner !== civId) seen.add(city.owner);
    if (vision) {
      for (const unit of Object.values(state.units)) {
        if (unit.owner !== civId && vision[unit.row * width + unit.col] === VIS_VISIBLE) seen.add(unit.owner);
      }
    }
    for (const other of seen) {
      if (state.civs[other] && !state.civs[other]!.defeated && !relationOf(state, civId, other)) ensureRelation(state, civId, other);
    }
  }
}

/**
 * Send home every unit standing where it may not: to its owner's nearest city (a ship,
 * to the nearest harbour). A unit aboard a ship goes with the ship. A civilisation with
 * nowhere to send it — no city, or a ship with no harbour — keeps it where it is.
 */
export function expelTrespassers(state: GameState, content: Content): GameEvent[] {
  const counts = new Map<string, number>();
  const touched = new Set<Id>();
  const units = Object.values(state.units).sort((a, b) => a.id - b.id);
  for (const unit of units) {
    if (unit.aboard !== NONE) continue;
    const at = tileIndex(state.map, unit.col, unit.row);
    if (mayEnter(state, unit.owner, at)) continue;
    const owner = state.map.owner[at]!;
    const sea = content.units.get(unit.type).domain === 'sea';
    let home: City | null = null;
    let best = Infinity;
    for (const city of Object.values(state.cities)) {
      if (city.owner !== unit.owner) continue;
      if (sea && !isCoastalCity(content, state.map, city)) continue;
      const d = wrappedDistance(state.map, at, tileIndex(state.map, city.col, city.row));
      if (d < best || (d === best && home && city.id < home.id)) {
        best = d;
        home = city;
      }
    }
    if (!home) continue;
    for (const passenger of Object.values(state.units)) {
      if (passenger.aboard === unit.id) {
        passenger.col = home.col;
        passenger.row = home.row;
      }
    }
    unit.col = home.col;
    unit.row = home.row;
    if (unit.orders?.kind === 'goto') unit.orders = null;
    const key = `${unit.owner}:${owner}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
    touched.add(unit.owner);
  }
  for (const civId of touched) recomputeVision(state, content, civId);
  return [...counts.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, count]) => {
      const [civId, from] = key.split(':').map(Number) as [number, number];
      return { t: 'UnitsExpelled', civId, from, count };
    });
}
